/**
 * Typed projection over the /v4/fido2/verify response.
 *
 * Ports `VerifiedSummary` from the native launchers so browser callers gate access
 * on the same rules as iOS and Android instead of hand-parsing the raw response.
 *
 * @module core/verified-summary
 */

/**
 * Typed view of a verification response.
 *
 * Field semantics match the native `VerifiedSummary` exactly. In particular, a predicate
 * that is `null` was **not requested** — it is not a failure. Only an explicit `false`
 * counts against the result.
 */
export interface VerifiedSummary {
  /**
   * Whether the verify endpoint returned a usable, error-free payload.
   */
  verified: boolean;

  /**
   * Face-match result from `predicates.face_match_pass`.
   * `null` when the check was not present in the response.
   */
  faceMatch: boolean | null;

  /**
   * Liveness / presentation-attack-detection result from `predicates.pad_pass`.
   * `null` when the check was not present in the response.
   */
  livenessPass: boolean | null;

  /**
   * Age-check result from `predicates.age_pass`.
   * `null` when no age predicate was requested.
   */
  agePass: boolean | null;

  /**
   * Non-null when the verify endpoint (or the decoder) returned an error instead of a
   * predicate payload. When set, treat the result as unverified.
   */
  errorDescription: string | null;

  /**
   * `true` only when `verified` is true, no error is present, and no predicate that IS
   * present failed. An absent predicate does not block the result.
   *
   * This is the value to gate access on.
   */
  allChecksPassed: boolean;

  /**
   * The raw decoded verify response, for fields not surfaced above.
   */
  verifiedData: Record<string, unknown> | null;
}

/**
 * Decoded biometric-proof predicates carried by the JWE payload.
 *
 * @see BACKEND_API.md "JWE Token Payload"
 */
interface PredicatePayload {
  predicates?: {
    face_match_pass?: unknown;
    pad_pass?: unknown;
    age_pass?: unknown;
    age_threshold?: unknown;
    assurance_level?: unknown;
  };
}

/**
 * Reads a value only if it is a real boolean.
 *
 * Anything else (missing, null, string, number) becomes `null` — "not present" rather
 * than a coerced pass/fail. Matches the native `as? Bool` behaviour.
 */
function optionalBoolean(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null;
}

/**
 * Computes `allChecksPassed` from the individual fields.
 *
 * Mirrors the native rule: require `verified` and no error, then reject only on an
 * explicit `false`. A `null` predicate is not a failure.
 */
function computeAllChecksPassed(fields: {
  verified: boolean;
  faceMatch: boolean | null;
  livenessPass: boolean | null;
  agePass: boolean | null;
  errorDescription: string | null;
}): boolean {
  if (fields.errorDescription !== null) return false;
  if (!fields.verified) return false;
  if (fields.faceMatch === false) return false;
  if (fields.livenessPass === false) return false;
  if (fields.agePass === false) return false;
  return true;
}

/**
 * Builds a typed {@link VerifiedSummary} from a raw verify response.
 *
 * The response nests the predicate payload as a JSON *string* under `payload.payload`,
 * which is parsed here. When that structure is missing the summary still reports
 * `verified`, with all predicates `null` — the same degradation as native.
 *
 * @param raw - Decoded JSON body from the verify endpoint, or null/undefined
 * @returns A typed summary, or `null` when there was no response at all
 *
 * @example
 * ```typescript
 * const summary = parseVerifiedSummary(response);
 * if (summary?.allChecksPassed) {
 *   grantAccess();
 * } else {
 *   denyAccess(summary?.errorDescription ?? 'checks did not pass');
 * }
 * ```
 */
export function parseVerifiedSummary(raw: unknown): VerifiedSummary | null {
  if (raw === null || raw === undefined || typeof raw !== 'object') {
    return null;
  }

  const response = raw as Record<string, unknown>;

  // Error responses short-circuit: the endpoint returned a failure instead of predicates.
  // Client-side failures are folded into this same shape ({ error: message }) so callers
  // have one path to check, matching native.
  if (typeof response.error === 'string') {
    return {
      verified: false,
      faceMatch: null,
      livenessPass: null,
      agePass: null,
      errorDescription: response.error,
      allChecksPassed: false,
      verifiedData: response
    };
  }

  const verified = response.verified === true;

  // Predicates live in a JSON string nested two levels deep: payload.payload
  let predicates: PredicatePayload['predicates'];
  const payloadContainer = response.payload as Record<string, unknown> | undefined;
  const payloadString = payloadContainer?.payload;

  if (typeof payloadString === 'string') {
    try {
      const parsed = JSON.parse(payloadString) as PredicatePayload;
      if (parsed && typeof parsed === 'object' && typeof parsed.predicates === 'object') {
        predicates = parsed.predicates ?? undefined;
      }
    } catch {
      // Undecodable payload — fall through with null predicates rather than failing the
      // whole summary. `verified` still reflects what the endpoint said.
    }
  }

  const faceMatch = optionalBoolean(predicates?.face_match_pass);
  const livenessPass = optionalBoolean(predicates?.pad_pass);
  const agePass = optionalBoolean(predicates?.age_pass);

  return {
    verified,
    faceMatch,
    livenessPass,
    agePass,
    errorDescription: null,
    allChecksPassed: computeAllChecksPassed({
      verified,
      faceMatch,
      livenessPass,
      agePass,
      errorDescription: null
    }),
    verifiedData: response
  };
}
